/**
 * Auth Store - Zustand
 * In-memory token storage for XSS defense, hydration state machine for SSR safety
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { UserResponse, TokenResponse, AuthEnvelope } from '../types/rest';

/**
 * Hydration status - prevents UI flicker during token silent refresh on page reload
 */
export type AuthHydrationStatus = 'idle' | 'loading' | 'authenticated' | 'unauthenticated';

/**
 * Auth store state
 */
export interface AuthState {
  // User & tokens
  user: UserResponse | null;
  accessToken: string | null;
  refreshToken: string | null;
  expiresIn: number | null;
  
  // Hydration state machine
  hydrationStatus: AuthHydrationStatus;
  isHydrated: boolean;
  
  // Actions
  setAuth: (auth: AuthEnvelope) => void;
  setUser: (user: UserResponse) => void;
  setTokens: (tokens: TokenResponse) => void;
  clearAuth: () => void;
  updateAccessToken: (token: string, expiresIn: number) => void;
  
  // Hydration
  hydrate: () => Promise<void>;
  setHydrationStatus: (status: AuthHydrationStatus) => void;
}

/**
 * In-memory storage for access token (XSS defense)
 * Refresh token handled via httpOnly cookie by backend
 */
let memoryAccessToken: string | null = null;
let memoryRefreshToken: string | null = null;
let memoryUser: UserResponse | null = null;
let memoryExpiresIn: number | null = null;

/**
 * Create auth store with hydration support
 * Uses in-memory storage for access token, persists only user for rehydration
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      accessToken: null,
      refreshToken: null,
      expiresIn: null,
      hydrationStatus: 'idle',
      isHydrated: false,
      
      // Set full auth envelope (after login/register)
      setAuth: (auth: AuthEnvelope) => {
        memoryAccessToken = auth.tokens.access_token;
        memoryRefreshToken = auth.tokens.refresh_token;
        memoryUser = auth.user;
        memoryExpiresIn = auth.tokens.expires_in;
        
        set({
          user: auth.user,
          accessToken: auth.tokens.access_token,
          refreshToken: auth.tokens.refresh_token,
          expiresIn: auth.tokens.expires_in,
          hydrationStatus: 'authenticated',
          isHydrated: true,
        });
      },
      
      // Set user only
      setUser: (user: UserResponse) => {
        memoryUser = user;
        set({ user });
      },
      
      // Set tokens (after refresh)
      setTokens: (tokens: TokenResponse) => {
        memoryAccessToken = tokens.access_token;
        memoryRefreshToken = tokens.refresh_token;
        memoryExpiresIn = tokens.expires_in;
        
        set({
          accessToken: tokens.access_token,
          refreshToken: tokens.refresh_token,
          expiresIn: tokens.expires_in,
        });
      },
      
      // Clear all auth state
      clearAuth: () => {
        memoryAccessToken = null;
        memoryRefreshToken = null;
        memoryUser = null;
        memoryExpiresIn = null;
        
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          expiresIn: null,
          hydrationStatus: 'unauthenticated',
          isHydrated: true,
        });
      },
      
      // Update access token (called by axios interceptor on refresh)
      updateAccessToken: (token: string, expiresIn: number) => {
        memoryAccessToken = token;
        memoryExpiresIn = expiresIn;
        
        set({
          accessToken: token,
          expiresIn,
        });
      },
      
      // Hydrate from storage (called on app init)
      hydrate: async () => {
        const state = get();
        
        // Already hydrated
        if (state.isHydrated) {
          return;
        }
        
        set({ hydrationStatus: 'loading' });
        
        try {
          // Try to restore from memory (for same-tab navigation)
          if (memoryAccessToken && memoryUser) {
            set({
              user: memoryUser,
              accessToken: memoryAccessToken,
              refreshToken: memoryRefreshToken,
              expiresIn: memoryExpiresIn,
              hydrationStatus: 'authenticated',
              isHydrated: true,
            });
            return;
          }
          
          // Try to restore from persisted user (cross-tab)
          // Note: accessToken is NOT persisted - must be refreshed via cookie
          const stored = state.user;
          if (stored) {
            set({
              user: stored,
              hydrationStatus: 'loading', // Need to refresh token
              isHydrated: true,
            });
            
            // Trigger token refresh via axios interceptor
            // This will call updateAccessToken when successful
            return;
          }
          
          // No auth found
          set({
            hydrationStatus: 'unauthenticated',
            isHydrated: true,
          });
        } catch (error) {
          console.error('[AuthStore] Hydration failed:', error);
          set({
            hydrationStatus: 'unauthenticated',
            isHydrated: true,
          });
        }
      },
      
      // Set hydration status manually
      setHydrationStatus: (status: AuthHydrationStatus) => {
        set({ hydrationStatus: status });
      },
    }),
    {
      name: 'fog-chess-auth',
      // Only persist user (non-sensitive) - tokens stay in memory
      partialize: (state) => ({
        user: state.user,
      }),
      // Custom storage that doesn't persist tokens
      storage: createJSONStorage(() => ({
        getItem: (name: string) => {
          const data = localStorage.getItem(name);
          if (!data) return null;
          try {
            const parsed = JSON.parse(data);
            // Strip tokens from persisted data
            return JSON.stringify({
              state: {
                ...parsed.state,
                accessToken: null,
                refreshToken: null,
                expiresIn: null,
                hydrationStatus: 'idle',
                isHydrated: false,
              },
            });
          } catch {
            return null;
          }
        },
        setItem: (name: string, value: string) => {
          localStorage.setItem(name, value);
        },
        removeItem: (name: string) => {
          localStorage.removeItem(name);
        },
      })),
      // Skip hydration on server
      skipHydration: typeof window === 'undefined',
    }
  )
);

/**
 * Selectors for common use cases
 */

// Check if user is authenticated (has valid access token in memory)
export const useIsAuthenticated = () => 
  useAuthStore((state) => !!state.accessToken && !!state.user);

// Get user
export const useUser = () => 
  useAuthStore((state) => state.user);

// Get access token (for axios interceptor)
export const useAccessToken = () => 
  useAuthStore((state) => state.accessToken);

// Get hydration status
export const useHydrationStatus = () => 
  useAuthStore((state) => state.hydrationStatus);

// Check if hydrated
export const useIsHydrated = () => 
  useAuthStore((state) => state.isHydrated);

// Combined auth state selector
export const useAuth = () => {
  const user = useAuthStore((state) => state.user);
  const accessToken = useAuthStore((state) => state.accessToken);
  const hydrationStatus = useAuthStore((state) => state.hydrationStatus);
  const isHydrated = useAuthStore((state) => state.isHydrated);
  const setAuth = useAuthStore((state) => state.setAuth);
  const clearAuth = useAuthStore((state) => state.clearAuth);
  const updateAccessToken = useAuthStore((state) => state.updateAccessToken);
  const hydrate = useAuthStore((state) => state.hydrate);
  
  return {
    user,
    accessToken,
    isAuthenticated: !!accessToken && !!user,
    hydrationStatus,
    isHydrated,
    setAuth,
    clearAuth,
    updateAccessToken,
    hydrate,
  };
};

/**
 * Initialize auth store on app startup
 * Call once in root layout or App component
 */
export async function initializeAuth(): Promise<void> {
  await useAuthStore.getState().hydrate();
}

/**
 * Get current access token without subscribing
 * For use in axios interceptors outside React context
 */
export function getCurrentAccessToken(): string | null {
  return memoryAccessToken;
}

/**
 * Set access token without subscribing
 * For use in axios interceptors outside React context
 */
export function setCurrentAccessToken(token: string | null, expiresIn: number | null = null): void {
  memoryAccessToken = token;
  memoryExpiresIn = expiresIn;
  
  // Also update store if it exists
  try {
    useAuthStore.getState().updateAccessToken(token ?? '', expiresIn ?? 0);
  } catch {
    // Store not initialized yet
  }
}

/**
 * Clear auth without subscribing
 */
export function clearCurrentAuth(): void {
  memoryAccessToken = null;
  memoryRefreshToken = null;
  memoryUser = null;
  memoryExpiresIn = null;
  
  try {
    useAuthStore.getState().clearAuth();
  } catch {
    // Store not initialized yet
  }
}